/**
 * Whether the requesting user can manage a workflow record's SharePoint files.
 *
 * The server resolves this policy for the records menu and independently
 * re-resolves it for every file operation, so a stale client copy never grants
 * access.
 */
export type WorkflowFileManagementOptions = {
  /** Whether current configuration, lifecycle, and locks allow file management. */
  enabled: boolean
  /** Whether the user holds a matching grant or implicit elevation. */
  authorized: boolean
  /** Why file management is unavailable, or `null` when it is fully available. */
  disabledReason: WorkflowFileErrorReason | null
}

/**
 * Client-safe failure reasons for workflow file operations.
 *
 * Provider URLs, credentials, and raw provider messages never cross this
 * boundary.
 */
export type WorkflowFileErrorReason =
  | 'inactive'
  | 'configurationDisabled'
  | 'locked'
  | 'unauthorized'
  | 'folderMissing'
  | 'notFound'
  | 'outsideRoot'
  | 'notFolder'
  | 'invalidInput'
  | 'unsupportedContentType'
  | 'unsupportedItem'
  | 'bodyTooLarge'
  | 'conflict'
  | 'busy'
  | 'sessionLost'
  | 'providerFailure'

/**
 * Client-safe metadata for one file or folder beneath a record folder.
 *
 * Unsupported provider items, such as packages, are listed without management
 * actions.
 */
export type WorkflowFileItem = {
  /** Stable provider item ID. */
  id: string
  parentId: string | null
  name: string
  kind: 'file' | 'folder' | 'unsupported'
  /** Size in bytes. */
  size: number
  /** ISO 8601 last-modified timestamp. */
  modified: string | null
  webUrl: string | null
  /** Provider entity tag used for conditional removal. */
  eTag: string | null
}

/**
 * Identifies a record's verified SharePoint folder for the file manager.
 *
 * The folder template only supplies a display label; when rendering it fails,
 * the verified folder remains usable.
 */
export type WorkflowFileMetadata = {
  capability: WorkflowFileManagementOptions
  /** The verified record folder. */
  root: WorkflowFileItem
  /** The folder template rendered for display, or `null` when unavailable. */
  renderedLabel: string | null
  /** Explains a `null` {@link WorkflowFileMetadata.renderedLabel}. */
  labelReason: 'labelUnavailable' | null
}

/**
 * The immediate children of one folder.
 *
 * Expanding a nested folder fetches its children separately.
 */
export type WorkflowFileChildren = {
  folderId: string
  items: WorkflowFileItem[]
}

/**
 * Server-confirmed progress for one upload attempt.
 *
 * Only a successful provider response from this attempt establishes
 * completion; matching external file metadata never does.
 */
export type WorkflowFileUploadProgress = {
  /** Opaque server session ID, or `null` when no session was staged. */
  sessionId: string | null
  /** Bytes accepted by the provider. */
  offset: number
  /** Total bytes in the file. */
  size: number
  /**
   * `uploading` and `staged` attempts remain active; `staged` means every byte
   * was accepted but the commit has not been confirmed. `unverified` means the
   * attempt may have committed but the server could not confirm it.
   */
  state:
    | 'uploading'
    | 'staged'
    | 'uploaded'
    | 'replaced'
    | 'conflict'
    | 'unverified'
    | 'failed'
    | 'cancelled'
  /** The committed item once the upload is confirmed. */
  item: WorkflowFileItem | null
  message: string | null
}

/**
 * Per-target outcomes of one removal batch.
 *
 * Outcomes cover effective targets only, omitting selected descendants that a
 * selected ancestor folder already covers.
 */
export type WorkflowFileRemoveResult = {
  outcomes: Array<{
    id: string
    state:
      | 'removed'
      | 'alreadyAbsent'
      | 'conflict'
      | 'failed'
      | 'refreshRequired'
    message: string | null
  }>
}

/**
 * Maximum bytes per upload fragment.
 *
 * Transport bounds limit each request, never the size of the user's queue.
 */
export const WORKFLOW_FILE_CHUNK_SIZE = 5 * 1024 * 1024

/** Maximum item IDs per removal request. */
export const WORKFLOW_FILE_REMOVE_BATCH_SIZE = 100
